import type {
  ArtifactProjection,
  BootstrapPayload,
  ExperimentProjection,
  ExperimentSessionLink,
  ExperimentSessionsPayload,
  ExperimentTopicLink,
  FilePreview,
  GpuDeviceSnapshot,
  GpuHistoryPoint,
  GuidanceCreateInput,
  GuidanceView,
  MemoryGraphJobView,
  MemoryGraphPayload,
  MemoryImportReport,
  LiteraturePaper,
  LiteratureMemoryPromotion,
  LiteraturePaperStatus,
  LiteraturePayload,
  LiteratureProfile,
  LiteratureTask,
  MetricCatalogRun,
  ResearchReportSchedule,
  MetricSeries,
  ResearchReportDetail,
  ResearchReportSummary,
  ResearchCompletionState,
  ResearchPaperJob,
  ResearchPaperVersion,
  ProposalLineagePayload,
  ResearchTopicProjection,
  RunProjection,
  SystemHealth,
  ThreadPreference,
  TopicExperimentsPayload,
  CodexApiKeyProviderInput,
  CodexAuthView,
  CodexChatComposerSettings,
  CodexChatEditResult,
  CodexChatGoal,
  CodexChatGoalStatus,
  CodexChatOptionsView,
  CodexChatStatusView,
  CodexChatView,
  CodexFileMatch,
  CodexSkillView,
  CodexUploadedFile,
  CodexResearchStartInput,
  CodexResearchStartResult,
  ResearchIntakeCommitInput,
  ResearchIntakeCommitResult,
  ResearchIntakeCreateInput,
  ResearchIntakeDiscussInput,
  ResearchIntakeSession,
  ResearchIntakeUpdateInput,
  CandidatePlannerSession,
  GuidanceInboxView,
  GuidanceItemView,
  GuidancePublishInput,
  ResearchNodeRef,
  TerminalAttachTicket,
  TerminalSessionListPayload,
  TerminalSessionView
} from "@roboscientist/contracts";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string | null = null,
    readonly details?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body != null && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const response = await fetch(url, {
    ...init,
    headers
  });
  const body = await response.json().catch(() => ({})) as { error?: string; code?: string; details?: unknown };
  if (!response.ok) {
    throw new ApiError(
      response.status,
      body.error ?? `${response.status} ${response.statusText}`,
      typeof body.code === "string" ? body.code : null,
      body.details
    );
  }
  return body as T;
}

const fileAsBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(reader.error ?? new Error("Unable to read the selected file"));
  reader.onload = () => {
    const result = typeof reader.result === "string" ? reader.result : "";
    const marker = result.indexOf(",");
    if (marker < 0) reject(new Error("Unable to encode the selected file"));
    else resolve(result.slice(marker + 1));
  };
  reader.readAsDataURL(file);
});

export type ExperimentFilters = {
  topicId?: string;
  status?: string;
  kind?: string;
  search?: string;
  from?: string;
  to?: string;
  unassigned?: boolean;
  sessionId?: string;
  threadId?: string;
  shared?: boolean;
  includeArchived?: boolean;
  resolutionState?: "resolved" | "pending" | "invalid";
};

export type ResearchReportSummaryRequest = {
  sourceReportId: string | null;
  seriesId: string;
  rootThreadId: string;
  writerThreadId: string;
  operationId: string;
  trigger: "manual" | "schedule";
  requestedAt: string;
  updateIntervalMinutes: number;
  deliveryMethod: "ephemeral-fork" | "ephemeral-task";
  turnId: string | null;
  deduplicated: boolean;
};

export const api = {
  bootstrap: () => request<BootstrapPayload>("/api/v1/bootstrap"),
  codexAuth: () => request<{ auth: CodexAuthView }>("/api/v1/codex/auth", { cache: "no-store" }),
  startCodexDeviceLogin: () => request<{ auth: CodexAuthView }>(
    "/api/v1/codex/auth/device-code",
    { method: "POST", body: "{}" }
  ),
  loginCodexWithApiKey: (apiKey: string, provider: CodexApiKeyProviderInput) => request<{ auth: CodexAuthView }>(
    "/api/v1/codex/auth/api-key",
    { method: "POST", body: JSON.stringify({ apiKey, provider }) }
  ),
  cancelCodexLogin: () => request<{ auth: CodexAuthView }>(
    "/api/v1/codex/auth/pending",
    { method: "DELETE", body: "{}" }
  ),
  logoutCodex: () => request<{ auth: CodexAuthView }>(
    "/api/v1/codex/auth/logout",
    { method: "POST", body: "{}" }
  ),
  terminalSessions: () => request<TerminalSessionListPayload>("/api/v1/terminal/sessions"),
  createTerminalSession: (cols = 120, rows = 32) => request<{ session: TerminalSessionView }>(
    "/api/v1/terminal/sessions",
    { method: "POST", body: JSON.stringify({ cols, rows }) }
  ),
  createTerminalAttachment: (sessionId: string) => request<TerminalAttachTicket>(
    `/api/v1/terminal/sessions/${encodeURIComponent(sessionId)}/attachments`,
    { method: "POST", body: "{}" }
  ),
  terminateTerminalSession: (sessionId: string) => request<{ session: TerminalSessionView }>(
    `/api/v1/terminal/sessions/${encodeURIComponent(sessionId)}`,
    { method: "DELETE", body: "{}" }
  ),
  terminalSocketUrl: (sessionId: string) => {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/api/v1/terminal/sessions/${encodeURIComponent(sessionId)}/stream`;
  },
  setThreadHidden: (threadId: string, hidden: boolean) => request<{ preference: ThreadPreference }>(
    `/api/v1/threads/${encodeURIComponent(threadId)}/preferences`,
    { method: "PATCH", body: JSON.stringify({ hidden }) }
  ),
  topicExperiments: (topicId: string) => request<TopicExperimentsPayload>(
    `/api/v1/topics/${encodeURIComponent(topicId)}/experiments`
  ),
  requestResearchCompletion: (topicId: string) => request<{ state: ResearchCompletionState }>(
    `/api/v1/topics/${encodeURIComponent(topicId)}/completion`,
    { method: "POST", body: "{}" }
  ),
  reopenResearch: (topicId: string) => request<{ state: ResearchCompletionState }>(
    `/api/v1/topics/${encodeURIComponent(topicId)}/reopen`,
    { method: "POST", body: "{}" }
  ),
  generateResearchPaper: (topicId: string) => request<{ job: ResearchPaperJob | null }>(
    `/api/v1/topics/${encodeURIComponent(topicId)}/paper-jobs`,
    { method: "POST", body: "{}" }
  ),
  retryPaperJob: (jobId: string) => request<{ job: ResearchPaperJob }>(
    `/api/v1/paper-jobs/${encodeURIComponent(jobId)}/retry`,
    { method: "POST", body: "{}" }
  ),
  revisePaperJob: (jobId: string) => request<{ job: ResearchPaperJob }>(
    `/api/v1/paper-jobs/${encodeURIComponent(jobId)}/revise`,
    { method: "POST", body: "{}" }
  ),
  cancelPaperJob: (jobId: string) => request<{ job: ResearchPaperJob }>(
    `/api/v1/paper-jobs/${encodeURIComponent(jobId)}/cancel`,
    { method: "POST", body: "{}" }
  ),
  researchPaper: (paperId: string) => request<{ paper: ResearchPaperVersion }>(
    `/api/v1/papers/${encodeURIComponent(paperId)}`
  ),
  researchPaperPdfUrl: (paperId: string) => `/api/v1/papers/${encodeURIComponent(paperId)}/pdf`,
  researchPaperSourceUrl: (paperId: string) => `/api/v1/papers/${encodeURIComponent(paperId)}/source`,
  researchPaperBuildLogUrl: (paperId: string) => `/api/v1/papers/${encodeURIComponent(paperId)}/build-log`,
  gallerySnapshotDownloadUrl: (topicId: string) =>
    `/api/v1/topics/${encodeURIComponent(topicId)}/gallery-snapshot`,
  proposalLineage: (proposalId: string) => request<ProposalLineagePayload>(
    `/api/v1/proposals/${encodeURIComponent(proposalId)}/lineage`
  ),
  guidanceInbox: (proposalId: string) => request<{ inbox: GuidanceInboxView }>(
    `/api/v1/proposals/${encodeURIComponent(proposalId)}/inbox`
  ),
  publishInboxGuidance: (proposalId: string, input: GuidancePublishInput) => request<{ item: GuidanceItemView }>(
    `/api/v1/proposals/${encodeURIComponent(proposalId)}/inbox`,
    { method: "POST", body: JSON.stringify(input) }
  ),
  applyInboxGuidanceNow: (itemId: string) => request<{ item: GuidanceItemView }>(
    `/api/v1/inbox/${encodeURIComponent(itemId)}/apply-now`,
    { method: "POST", body: "{}" }
  ),
  rejectInboxGuidance: (itemId: string, reason?: string) => request<{ item: GuidanceItemView }>(
    `/api/v1/inbox/${encodeURIComponent(itemId)}/reject`,
    { method: "POST", body: JSON.stringify(reason ? { reason } : {}) }
  ),
  createCandidatePlanner: (proposalId: string, sourceNodeRef: ResearchNodeRef) => request<{ session: CandidatePlannerSession }>(
    `/api/v1/proposals/${encodeURIComponent(proposalId)}/candidate-planners`,
    { method: "POST", body: JSON.stringify({ sourceNodeRef }) }
  ),
  candidatePlanner: (sessionId: string) => request<{ session: CandidatePlannerSession }>(
    `/api/v1/candidate-planners/${encodeURIComponent(sessionId)}`
  ),
  discussCandidatePlanner: (sessionId: string, message: string) => request<{ session: CandidatePlannerSession }>(
    `/api/v1/candidate-planners/${encodeURIComponent(sessionId)}/messages`,
    { method: "POST", body: JSON.stringify({ message }) }
  ),
  acceptCandidate: (sessionId: string, candidateId: string) => request<{ session: CandidatePlannerSession }>(
    `/api/v1/candidate-planners/${encodeURIComponent(sessionId)}/candidates/${encodeURIComponent(candidateId)}/accept`,
    { method: "POST", body: "{}" }
  ),
  rejectCandidate: (sessionId: string, candidateId: string) => request<{ session: CandidatePlannerSession }>(
    `/api/v1/candidate-planners/${encodeURIComponent(sessionId)}/candidates/${encodeURIComponent(candidateId)}/reject`,
    { method: "POST", body: "{}" }
  ),
  closeCandidatePlanner: (sessionId: string) => request<void>(
    `/api/v1/candidate-planners/${encodeURIComponent(sessionId)}`,
    { method: "DELETE" }
  ),
  researchReports: (filters: { threadId?: string; experimentId?: string; seriesId?: string } | string = {}) => {
    const normalized = typeof filters === "string" ? { threadId: filters } : filters;
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(normalized)) {
      if (value) query.set(key, value);
    }
    return request<{ reports: ResearchReportSummary[] }>(
      `/api/v1/reports${query.size ? `?${query.toString()}` : ""}`
    );
  },
  researchReport: (id: string) => request<{ report: ResearchReportDetail }>(
    `/api/v1/reports/${encodeURIComponent(id)}`
  ),
  researchReportDownloadUrl: (id: string) =>
    `/api/v1/reports/${encodeURIComponent(id)}/download`,
  summarizeResearchReport: (id: string, updateIntervalMinutes: number, resumeFinal = false) => request<{ request: ResearchReportSummaryRequest }>(
    `/api/v1/reports/${encodeURIComponent(id)}/summarize`,
    { method: "POST", body: JSON.stringify({ updateIntervalMinutes, resumeFinal }) }
  ),
  researchReportSchedule: (rootThreadId: string) => request<{ schedule: ResearchReportSchedule | null }>(
    `/api/v1/report-schedules/${encodeURIComponent(rootThreadId)}`
  ),
  updateResearchReportSchedule: (rootThreadId: string, updateIntervalMinutes: number) => request<{ schedule: ResearchReportSchedule }>(
    `/api/v1/report-schedules/${encodeURIComponent(rootThreadId)}`,
    { method: "PATCH", body: JSON.stringify({ updateIntervalMinutes }) }
  ),
  summarizeResearchThread: (rootThreadId: string, updateIntervalMinutes: number, resumeFinal = false) => request<{ request: ResearchReportSummaryRequest; schedule: ResearchReportSchedule | null }>(
    `/api/v1/report-schedules/${encodeURIComponent(rootThreadId)}/summarize`,
    { method: "POST", body: JSON.stringify({ updateIntervalMinutes, resumeFinal }) }
  ),
  experiments: (filters: ExperimentFilters = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value == null || value === "" || value === false) continue;
      query.set(key, String(value));
    }
    return request<{
      experiments: ExperimentProjection[];
      runs: RunProjection[];
      links: ExperimentTopicLink[];
      sessionLinks: ExperimentSessionLink[];
      topics: ResearchTopicProjection[];
    }>(`/api/v1/experiments${query.size ? `?${query}` : ""}`);
  },
  experiment: (id: string) => request<{
    experiment: ExperimentProjection;
    runs: RunProjection[];
  }>(`/api/v1/experiments/${encodeURIComponent(id)}`),
  experimentSessions: (id: string) => request<ExperimentSessionsPayload>(
    `/api/v1/experiments/${encodeURIComponent(id)}/sessions`
  ),
  setExperimentTopic: (experimentId: string, topicId: string | null) => request<{ link: ExperimentTopicLink }>(
    `/api/v1/experiments/${encodeURIComponent(experimentId)}/topic`,
    { method: "PUT", body: JSON.stringify({ topicId }) }
  ),
  setExperimentArchived: (experimentId: string, archived: boolean) => request<{ experiment: ExperimentProjection }>(
    `/api/v1/experiments/${encodeURIComponent(experimentId)}`,
    { method: "PATCH", body: JSON.stringify({ archived }) }
  ),
  run: (id: string) => request<{ run: RunProjection; artifacts: ArtifactProjection[] }>(
    `/api/v1/runs/${encodeURIComponent(id)}`
  ),
  runLog: (id: string) => request<FilePreview>(
    `/api/v1/runs/${encodeURIComponent(id)}/log`
  ),
  stopActiveRun: (id: string) => request<{ runId: string; status: "cancelling"; providerResult: Record<string, unknown> }>(
    `/api/v1/runs/${encodeURIComponent(id)}/stop`,
    { method: "POST", body: JSON.stringify({ confirmation: "STOP_ACTIVE_RUN" }) }
  ),
  metrics: (id: string, names: string[] = []) => request<{ runId: string; series: MetricSeries[] }>(
    `/api/v1/runs/${encodeURIComponent(id)}/metrics?names=${encodeURIComponent(names.join(","))}`
  ),
  metricCatalog: (kind?: RunProjection["kind"]) => request<{ runs: MetricCatalogRun[] }>(
    `/api/v1/metrics/catalog${kind ? `?kind=${encodeURIComponent(kind)}` : ""}`
  ),
  gpus: (from?: string, to?: string, maxPoints = 1_000) => {
    const query = new URLSearchParams({ maxPoints: String(maxPoints) });
    if (from) query.set("from", from);
    if (to) query.set("to", to);
    return request<{
      current: GpuDeviceSnapshot[];
      history: GpuHistoryPoint[];
      health: BootstrapPayload["health"]["resources"];
    }>(`/api/v1/resources/gpus?${query}`);
  },
  memory: (kind?: string) => request<{
    records: GuidanceView[];
    health: BootstrapPayload["health"]["memory"];
    error?: string;
  }>(`/api/v1/memory?includeArchived=true&limit=1000${kind ? `&kind=${encodeURIComponent(kind)}` : ""}`),
  importMemory: async (file: File) => request<{ report: MemoryImportReport }>(
    "/api/v1/memory/import",
    { method: "POST", body: JSON.stringify({ fileName: file.name, dataBase64: await fileAsBase64(file) }) }
  ),
  memoryGraph: () => request<MemoryGraphPayload>("/api/v1/memory-graph"),
  updateMemoryGraph: (input: { prompt?: string } = {}) => request<{ job: MemoryGraphJobView }>(
    "/api/v1/memory-graph/update",
    { method: "POST", body: JSON.stringify(input) }
  ),
  literature: () => request<LiteraturePayload>("/api/v1/literature"),
  literaturePaper: (id: string) => request<{ paper: LiteraturePaper }>(
    `/api/v1/literature/papers/${encodeURIComponent(id)}`
  ),
  discoverLiterature: (query: string) => request<{ task: LiteratureTask }>(
    "/api/v1/literature/discover",
    { method: "POST", body: JSON.stringify({ query }) }
  ),
  uploadLiterature: async (file: File) => request<{ paper: LiteraturePaper; task: LiteratureTask | null }>(
    "/api/v1/literature/upload",
    { method: "POST", body: JSON.stringify({ fileName: file.name, dataBase64: await fileAsBase64(file) }) }
  ),
  setLiteraturePaperStatus: (id: string, status: LiteraturePaperStatus) => request<{ paper: LiteraturePaper }>(
    `/api/v1/literature/papers/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify({ status }) }
  ),
  summarizeLiteraturePaper: (id: string) => request<{ task: LiteratureTask }>(
    `/api/v1/literature/papers/${encodeURIComponent(id)}/summarize`,
    { method: "POST", body: "{}" }
  ),
  promoteLiteraturePaper: (id: string) => request<LiteratureMemoryPromotion>(
    `/api/v1/literature/papers/${encodeURIComponent(id)}/memory`,
    { method: "PUT", body: "{}" }
  ),
  interruptLiteratureTask: (id: string) => request<{ task: LiteratureTask }>(
    `/api/v1/literature/tasks/${encodeURIComponent(id)}/interrupt`,
    { method: "POST", body: "{}" }
  ),
  saveLiteratureProfile: (profile: Pick<LiteratureProfile, "query" | "cadenceHours" | "enabled">) =>
    request<{ profile: LiteratureProfile }>(
      "/api/v1/literature/profile",
      { method: "PUT", body: JSON.stringify(profile) }
    ),
  literaturePdfUrl: (id: string) => `/api/v1/literature/papers/${encodeURIComponent(id)}/pdf`,
  createGuidance: (input: GuidanceCreateInput) => request<{ record: GuidanceView; delivery: "recorded_only" }>(
    "/api/v1/guidance",
    { method: "POST", body: JSON.stringify(input) }
  ),
  archiveMemory: (id: string) => request<{ record: GuidanceView }>(
    `/api/v1/memory/${encodeURIComponent(id)}/archive`,
    { method: "POST", body: "{}" }
  ),
  health: () => request<{
    capabilities: BootstrapPayload["capabilities"];
    health: SystemHealth;
    cursor: number;
    generatedAt: string;
  }>("/api/v1/system/health"),
  createCodexChat: (name?: string) => request<{ chat: CodexChatView }>(
    "/api/v1/codex-chats",
    { method: "POST", body: JSON.stringify(name ? { name } : {}) }
  ),
  startCodexResearch: (input: CodexResearchStartInput) =>
    request<CodexResearchStartResult>(
      "/api/v1/codex-chats/research",
      { method: "POST", body: JSON.stringify(input) }
    ),
  createResearchIntake: (input: ResearchIntakeCreateInput) => request<{ intake: ResearchIntakeSession }>(
    "/api/v1/research-intakes",
    { method: "POST", body: JSON.stringify(input) }
  ),
  researchIntake: (id: string) => request<{ intake: ResearchIntakeSession }>(
    `/api/v1/research-intakes/${encodeURIComponent(id)}`
  ),
  discussResearchIntake: (id: string, input: ResearchIntakeDiscussInput) =>
    request<{ intake: ResearchIntakeSession }>(
      `/api/v1/research-intakes/${encodeURIComponent(id)}/messages`,
      { method: "POST", body: JSON.stringify(input) }
    ),
  updateResearchIntake: (id: string, input: ResearchIntakeUpdateInput) =>
    request<{ intake: ResearchIntakeSession }>(
      `/api/v1/research-intakes/${encodeURIComponent(id)}/requirement`,
      { method: "PUT", body: JSON.stringify(input) }
    ),
  commitResearchIntake: (id: string, input: ResearchIntakeCommitInput) =>
    request<ResearchIntakeCommitResult>(
      `/api/v1/research-intakes/${encodeURIComponent(id)}/commit`,
      { method: "POST", body: JSON.stringify(input) }
    ),
  closeResearchIntake: (id: string) => request<{ intake: ResearchIntakeSession }>(
    `/api/v1/research-intakes/${encodeURIComponent(id)}`,
    { method: "DELETE" }
  ),
  codexChat: (threadId: string) => request<{ chat: CodexChatView }>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}`
  ),
  loadOlderCodexChatHistory: (threadId: string) => request<{ chat: CodexChatView }>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}/history/older`,
    { method: "POST", body: "{}" }
  ),
  setCodexChatArchived: (threadId: string, archived: boolean) => request<{
    threadId: string;
    archived: boolean;
    updatedAt: string;
  }>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}`,
    { method: "PATCH", body: JSON.stringify({ archived }) }
  ),
  codexChatOptions: () => request<{ options: CodexChatOptionsView }>(
    "/api/v1/codex-chats/options"
  ),
  createCodexSideChat: (mainThreadId: string) => request<{ chat: CodexChatView }>(
    `/api/v1/codex-chats/${encodeURIComponent(mainThreadId)}/side`,
    { method: "POST", body: "{}" }
  ),
  codexSideChat: (threadId: string, mainThreadId: string) => request<{ chat: CodexChatView }>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}/side?mainThreadId=${encodeURIComponent(mainThreadId)}`
  ),
  closeCodexSideChat: (threadId: string) => request<void>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}/side`,
    { method: "DELETE" }
  ),
  uploadCodexFile: async (file: File) => request<{ file: CodexUploadedFile }>(
    "/api/v1/codex-chats/uploads",
    { method: "POST", body: JSON.stringify({ fileName: file.name, dataBase64: await fileAsBase64(file) }) }
  ),
  sendCodexChatMessage: (
    threadId: string,
    message: string,
    clientMessageId: string,
    skills: string[],
    mentions: string[],
    settings?: CodexChatComposerSettings,
    acknowledgements?: string[]
  ) => request<{ chat: CodexChatView }>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}/messages`,
    { method: "POST", body: JSON.stringify({ message, clientMessageId, skills, mentions, settings, acknowledgements }) }
  ),
  stopCodexChat: (threadId: string) => request<{ chat: CodexChatView }>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}/stop`,
    { method: "POST", body: "{}" }
  ),
  codexChatStatus: (threadId: string) => request<{ status: CodexChatStatusView }>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}/status`
  ),
  codexChatGoal: (threadId: string) => request<{ goal: CodexChatGoal | null }>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}/goal`
  ),
  setCodexChatGoal: (
    threadId: string,
    input: { objective?: string; status?: CodexChatGoalStatus; tokenBudget?: number | null }
  ) => request<{ goal: CodexChatGoal }>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}/goal`,
    { method: "PUT", body: JSON.stringify(input) }
  ),
  clearCodexChatGoal: (threadId: string) => request<{ cleared: boolean }>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}/goal`,
    { method: "DELETE" }
  ),
  editLastCodexMessage: (
    threadId: string,
    expectedTurnId: string,
    expectedMessageId: string,
    message: string,
    clientMessageId: string,
    skills: string[],
    mentions: string[],
    settings?: CodexChatComposerSettings,
    acknowledgements?: string[]
  ) => request<CodexChatEditResult>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}/messages/last/edit`,
    {
      method: "POST",
      body: JSON.stringify({
        expectedTurnId,
        expectedMessageId,
        message,
        clientMessageId,
        skills,
        mentions,
        settings,
        acknowledgements
      })
    }
  ),
  codexSkills: (forceReload = false) => request<{ skills: CodexSkillView[] }>(
    `/api/v1/codex-chats/skills?forceReload=${forceReload}`
  ),
  codexFiles: (query: string) => request<{ files: CodexFileMatch[] }>(
    `/api/v1/codex-chats/files?query=${encodeURIComponent(query)}`
  ),
  respondCodexChatRequest: (
    threadId: string,
    requestId: string,
    response: { decision?: string; answers?: Record<string, string[]>; content?: unknown }
  ) => request<{ chat: CodexChatView }>(
    `/api/v1/codex-chats/${encodeURIComponent(threadId)}/requests/${encodeURIComponent(requestId)}/respond`,
    { method: "POST", body: JSON.stringify(response) }
  )
};
